import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import logoImage from '../assets/smuglergods.svg';
import { useAuth } from '../contexts/AuthContext';

const Navbar: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user, signOut, loading, role } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleSignOut = async () => {
    await signOut();
    setIsMobileMenuOpen(false);
    navigate('/');
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const navigationLinks = [
    { to: '/', label: 'Hjem' },
    { to: '/about', label: 'Om' },
    { to: '/events', label: 'Arrangementer' },
    { to: '/blog', label: 'Blogg' },
    { to: '/musikk', label: 'Musikk' },
    { to: '/nyheter', label: 'Nyheter' },
    { to: '/contact', label: 'Kontakt' },
  ];

  const isActive = (to: string) =>
    to === '/' ? location.pathname === '/' : location.pathname.startsWith(to);

  return (
    <nav
      className="sticky top-0 z-50 text-white"
      style={{
        background: 'rgba(8,8,8,0.88)',
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        borderBottom: '1px solid rgba(155,240,30,0.18)',
      }}
    >
      {/* Top accent line */}
      <div style={{ height: '2px', background: 'linear-gradient(to right, transparent, #9bf01e 30%, #9bf01e 70%, transparent)', opacity: 0.7 }} />

      <div className="w-full px-4 lg:px-8">
        <div className="flex justify-between items-center h-16">

          {/* Logo */}
          <Link to="/" className="flex items-center shrink-0">
            <img
              src={logoImage}
              alt="Smuglergods"
              className="h-9 md:h-11 w-auto logo-filter transition-opacity hover:opacity-80"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navigationLinks.map((link) => {
              const active = isActive(link.to);
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  className="relative px-3 py-1.5 text-xs font-bold tracking-[0.15em] uppercase transition-colors duration-150"
                  style={{ color: active ? '#9bf01e' : 'rgba(255,255,255,0.55)' }}
                  onMouseEnter={e => { if (!active) e.currentTarget.style.color = 'rgba(255,255,255,0.95)'; }}
                  onMouseLeave={e => { if (!active) e.currentTarget.style.color = 'rgba(255,255,255,0.55)'; }}
                >
                  {link.label}
                  {active && (
                    <span
                      className="absolute bottom-0 left-3 right-3"
                      style={{ height: '1.5px', background: '#9bf01e', borderRadius: '1px' }}
                    />
                  )}
                </Link>
              );
            })}
          </div>

          {/* Desktop Auth */}
          <div className="hidden lg:flex items-center gap-3">
            {!loading && (
              user ? (
                <>
                  {role === 'admin' && (
                    <Link
                      to="/admin"
                      className="text-xs font-bold tracking-[0.15em] uppercase transition-colors duration-150"
                      style={{ color: isActive('/admin') ? '#9bf01e' : 'rgba(155,240,30,0.5)' }}
                      onMouseEnter={e => e.currentTarget.style.color = '#9bf01e'}
                      onMouseLeave={e => { if (!isActive('/admin')) e.currentTarget.style.color = 'rgba(155,240,30,0.5)'; }}
                    >
                      Admin
                    </Link>
                  )}
                  <Link
                    to="/account"
                    className="text-xs font-bold tracking-[0.15em] uppercase transition-colors duration-150"
                    style={{ color: 'rgba(255,255,255,0.45)' }}
                    onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.95)'}
                    onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.45)'}
                  >
                    Konto
                  </Link>
                  <button
                    onClick={handleSignOut}
                    className="btn-secondary py-1.5 px-4 text-xs font-bold"
                  >
                    Logg ut
                  </button>
                </>
              ) : (
                <Link to="/login" className="btn-primary py-1.5 px-5 text-xs font-bold inline-block">
                  Logg inn
                </Link>
              )
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMobileMenu}
            className="lg:hidden flex flex-col justify-center items-center w-10 h-10 gap-[5px] focus:outline-none"
            aria-label="Toggle menu"
          >
            <span className={`block h-px transition-all duration-300 ${isMobileMenuOpen ? 'w-5 rotate-45 translate-y-[6px] bg-[#9bf01e]' : 'w-6 bg-white'}`} />
            <span className={`block h-px transition-all duration-300 ${isMobileMenuOpen ? 'w-0 opacity-0 bg-[#9bf01e]' : 'w-4 bg-white opacity-60'}`} />
            <span className={`block h-px transition-all duration-300 ${isMobileMenuOpen ? 'w-5 -rotate-45 -translate-y-[6px] bg-[#9bf01e]' : 'w-6 bg-white'}`} />
          </button>
        </div>

        {/* Mobile Navigation Menu */}
        <div
          className={`lg:hidden overflow-hidden transition-all duration-300 ease-in-out ${
            isMobileMenuOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="py-3 pb-5" style={{ borderTop: '1px solid rgba(155,240,30,0.15)' }}>
            {navigationLinks.map((link) => {
              const active = isActive(link.to);
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  className="flex items-center gap-3 py-2.5 px-1 text-sm font-bold tracking-[0.12em] uppercase transition-all duration-150"
                  style={{ color: active ? '#9bf01e' : 'rgba(255,255,255,0.5)' }}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <span style={{ width: '14px', height: '1px', background: active ? '#9bf01e' : 'rgba(255,255,255,0.2)', display: 'inline-block', flexShrink: 0 }} />
                  {link.label}
                </Link>
              );
            })}

            <div className="mt-4 pt-4 flex flex-col gap-2" style={{ borderTop: '1px solid rgba(155,240,30,0.1)' }}>
              {!loading && (
                user ? (
                  <>
                    {role === 'admin' && (
                      <Link
                        to="/admin"
                        className="text-xs font-bold tracking-widest uppercase py-2 px-1 transition-colors"
                        style={{ color: 'rgba(155,240,30,0.7)' }}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        Admin
                      </Link>
                    )}
                    <Link
                      to="/account"
                      className="text-xs font-bold tracking-widest uppercase py-2 px-1 transition-colors"
                      style={{ color: 'rgba(255,255,255,0.4)' }}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Min konto
                    </Link>
                    <button
                      onClick={handleSignOut}
                      className="btn-secondary py-2 px-4 text-xs font-bold text-left w-fit"
                    >
                      Logg ut
                    </button>
                  </>
                ) : (
                  <Link
                    to="/login"
                    className="btn-primary py-2 px-5 text-xs font-bold inline-block w-fit"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Logg inn
                  </Link>
                )
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar; 